/**
 * useOnboarding Hook
 *
 * Manages onboarding state:
 * - Handles onboarding form submission
 * - Updates auth context after success
 */

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/useAuth";
import { ApiError } from "@/services/api/errors";
import {
  type OnboardingCompanyData,
  submitOnboarding as submitOnboardingRequest,
} from "@/services/api/user-client";

interface UseOnboardingResult {
  submitOnboarding: (data: OnboardingCompanyData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export function useOnboarding(): UseOnboardingResult {
  const { user, profile, refreshProfile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const hasRefreshedRef = useRef(false);

  useEffect(() => {
    // Re-arm on every mount (not just once at module scope): under dev
    // StrictMode's mount -> cleanup -> re-mount double-invoke, the cleanup
    // below flips this to false and it must flip back to true on the
    // subsequent re-mount, or every submitOnboarding call after it silently
    // no-ops (state updates gated behind isMountedRef.current never land).
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // If profile claims onboarding incomplete, re-check once to avoid stale state.
  useEffect(() => {
    if (!user || !profile || profile.onboardingCompleted === true) {
      hasRefreshedRef.current = false;
      return;
    }

    if (hasRefreshedRef.current) {
      return;
    }

    hasRefreshedRef.current = true;
    refreshProfile().catch(() => {
      // Non-blocking: keep current state; backend will enforce anyway
    });
  }, [user, profile, refreshProfile]);

  const submitOnboarding = async (
    data: OnboardingCompanyData,
  ): Promise<void> => {
    if (isMountedRef.current) {
      setIsLoading(true);
      setError(null);
    }

    try {
      await submitOnboardingRequest(data);

      // Refresh user profile to update onboardingCompleted status
      if (isMountedRef.current) {
        await refreshProfile();
      }
    } catch (err) {
      // The onboarding endpoint's only documented 409 is "already
      // completed" (no distinguishing error code survives the shared
      // request() layer's error-code allowlist) — treat any 409 here as a
      // soft-success and re-sync the profile instead of surfacing an error.
      if (err instanceof ApiError && err.status === 409) {
        if (isMountedRef.current && user) {
          await refreshProfile().catch(() => {
            // Best-effort: onboarding already completed
          });
        }
        return;
      }

      const message = err instanceof Error ? err.message : "An error occurred";
      if (isMountedRef.current) {
        setError(message);
      }
      throw err;
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  return {
    submitOnboarding,
    isLoading,
    error,
  };
}
